import {
    Outlet,
    createFileRoute,
    redirect,
    useRouter,
} from '@tanstack/react-router'
import SessionStore from '../State/SessionStore'
import AppUI from '../Pages/Layout/AppUI'
import LoadingSpinner from '../Components/LoadingSpinner'
import Modals from '../Pages/Layout/Modals'
import { version as appVersion } from '../../package.json'
import ErrorComponent from '../Components/ErrorComponent'
import { processTRPCData } from '../Queries/fetchData'
import Page from '../Pages/Layout/Page'
import { migratedToV2 } from '../State/Permissions/HasPermissions'
import { Layout } from '../version2/layout/layout'
import { Switch } from 'antd'
import { useEffect, useState } from 'react'
import { setCache, GLOBAL_CACHE_KEY } from '@2/cache'
import { Toaster } from '../version2/components/ui/sonner'
import { SaveQueueProcessor } from '../version2/layout/save-queue'
import { useIsFetching, useQueries } from '@tanstack/react-query'
import LayoutStore from '../State/LayoutStore'

export const Route = createFileRoute('/_private')({
    beforeLoad: async ({ location, context }) => {
        if (!SessionStore.initialized) await SessionStore.initialize()
        if (SessionStore.initialized && !SessionStore.authenticated) {
            throw redirect({
                to: '/login',
                search: {
                    redirect: location.href,
                },
            })
        } else {
            try {
                const { queryClient, trpc } = context
                const appData = await queryClient.fetchQuery({
                    queryKey: ['appDataTRPC', SessionStore.userId],
                    queryFn: () => trpc.legacy.getAppData.query(),
                    staleTime: 1000 * 60 * 5,
                    cacheTime: 1000 * 60 * 30,
                    retry: 3,
                })

                processTRPCData(appData)

                try {
                    const cacheData = await queryClient.fetchQuery({
                        queryKey: GLOBAL_CACHE_KEY,
                        queryFn: () => trpc.global.getCache.query(),
                        staleTime: 1000 * 60 * 5,
                        cacheTime: 1000 * 60 * 30,
                    })
                    setCache(cacheData)
                } catch (error) {
                    throw new Error(
                        'Failed to load global cache: ' + error.message
                    )
                }
                return { appData }
            } catch (error) {
                if (
                    (error.response && error.response.status === 401) ||
                    error.message === 'GlobalCacheError' ||
                    (error instanceof Error &&
                        error.message.includes('Failed to load global cache'))
                ) {
                    throw redirect({
                        to: '/login',
                        search: {
                            redirect: location.href,
                        },
                    })
                } else {
                    console.error(
                        'Error during beforeLoad data fetching:',
                        error
                    )
                    throw error
                }
            }
        }
    },
    pendingComponent: LoadingSpinner,
    component: Wrapper,
    errorComponent: ({ error, reset }) => {
        if (error.message.includes('No CollectionLookup found')) {
            return (
                <ErrorComponent
                    error={
                        new Error(
                            'Application setup error: Could not process essential data. Please contact support.'
                        )
                    }
                    reset={reset}
                />
            )
        }
        return <ErrorComponent error={error} reset={reset} />
    },
})

function Wrapper() {
    const router = useRouter()
    const [v2, setV2] = useState(migratedToV2())
    const isDev = process.env.REACT_APP_ENV === 'development'
    const params = Route.useParams()
    const search = Route.useSearch()

    const isFetching = useIsFetching({
        queryKey: ['appData', SessionStore.userId],
    })

    useEffect(() => {
        LayoutStore.pageRendered = !isFetching
    }, [isFetching])

    if (isFetching) {
        return <LoadingSpinner />
    }

    if (migratedToV2()) {
        return (
            <>
                <SaveQueueProcessor />
                <Layout>
                    <Outlet />
                </Layout>
                <div
                    className="dont-print"
                    style={{
                        backgroundColor: '#ffffffcc',
                        fontWeight: 'bold',
                        fontSize: '0.75em',
                        position: 'absolute',
                        bottom: '0',
                        right: '1.75em',
                        height: '1.5em',
                        padding: '0 0.5em',
                        borderRadius: '0.25em',
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 100,
                    }}
                >
                    {isDev ? (
                        <Switch
                            className="mr-2"
                            checked={v2}
                            size="small"
                            onChange={(v) => {
                                SessionStore.setMigratedToV2(v)
                                setV2(v)
                            }}
                            checkedChildren="V2"
                            unCheckedChildren="V2"
                        />
                    ) : null}
                    {appVersion}
                </div>
            </>
        )
    }
    return (
        <>
            <Modals {...params} {...search} />
            <SaveQueueProcessor />
            {/* <Toaster visibleToasts={3} position="top-center" /> */}
            <AppUI>
                <Page>
                    <Outlet />
                </Page>
            </AppUI>
            <div
                className="dont-print"
                style={{
                    backgroundColor: '#ffffffcc',
                    fontWeight: 'bold',
                    fontSize: '0.75em',
                    position: 'absolute',
                    bottom: '0',
                    right: '1.75em',
                    height: '1.5em',
                    padding: '0 0.5em',
                    borderRadius: '0.25em',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: 100,
                }}
            >
                {isDev ? (
                    <Switch
                        className="mr-2"
                        checked={v2}
                        size="small"
                        onChange={(v) => {
                            setV2(v)
                            SessionStore.setMigratedToV2(v)
                            router.invalidate()
                            setTimeout(() => router.invalidate(), 0)
                        }}
                        checkedChildren="V2"
                        unCheckedChildren="V2"
                    />
                ) : null}
                {appVersion}
            </div>
        </>
    )
}
